/**
 * @file ApplicationDetails.js
 * WHAT: Application registry management interface with inline editing, compliance flag toggling, multi-filter support, and CSV import/export.
 * WHY: Applications are the organizational unit that links databases to business owners -- each attestation campaign targets applications, so this registry must be accurate for compliance reviews to reach the right stakeholders.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_CONFIG from '../config/api.config';
import { useSafeAsync, useFilters } from '../hooks';

/**
 * WHAT: Renders a filterable, sortable table of applications with edit modal for status/environment/type/owner/compliance flags, plus CSV import/export for bulk management.
 * WHY: Compliance teams need to maintain the application inventory with correct owners and compliance tags so that UAR campaigns can be properly scoped and attestation routing reaches the right business owners.
 */
const ApplicationDetails = ({ initialAppName = '' } = {}) => {
  // Use useSafeAsync for applications data
  const {
    data: applications,
    loading,
    error: fetchError,
    execute: fetchApplications
  } = useSafeAsync(
    async () => {
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('applications/'));
      return response.data || [];
    },
    [],
    { immediate: true }
  );

  // Color map for compliance badges
  const colorMap = ['primary', 'success', 'warning', 'info', 'secondary', 'danger', 'dark'];

  // Use useSafeAsync for compliance frameworks
  const {
    data: complianceTypes
  } = useSafeAsync(
    async () => {
      try {
        const response = await axios.get(API_CONFIG.getDatabasesEndpoint('config/compliance-frameworks/'));
        if (response.data.success) {
          return response.data.frameworks.map((fw, index) => ({
            key: fw.key,
            label: fw.label,
            color: colorMap[index % colorMap.length]
          }));
        }
      } catch (err) {
        console.error('Failed to fetch compliance frameworks:', err);
      }
      // Fallback
      return [
        { key: 'sox_compliant', label: 'SOX', color: 'primary' },
        { key: 'gdpr_compliant', label: 'GDPR', color: 'success' },
        { key: 'pci_compliant', label: 'PCI', color: 'warning' },
        { key: 'hipaa_compliant', label: 'HIPAA', color: 'info' },
        { key: 'iso27001_compliant', label: 'ISO27001', color: 'secondary' }
      ];
    },
    [],
    { immediate: true }
  );

  // Use useSafeAsync for enterprise users
  const {
    data: enterpriseUsers
  } = useSafeAsync(
    async () => {
      try {
        const response = await axios.get(API_CONFIG.getEnterpriseUsersEndpoint(''));
        return response.data.users || [];
      } catch (err) {
        console.error('Failed to fetch enterprise users:', err);
        return [];
      }
    },
    [],
    { immediate: true }
  );

  // Use useFilters hook for filter management
  const {
    filters,
    setFilter,
    clearAllFilters,
    toggleArrayFilter
  } = useFilters({
    appName: initialAppName || '',
    status: '',
    environment: '',
    appType: '',
    compliance_filters: []
  });

  // Local UI state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [editFormData, setEditFormData] = useState({
    app_status: '',
    app_environment: '',
    app_type: '',
    app_version: '',
    app_owner: '',
    notes: '',
    compliance_flags: {}
  });
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = React.useRef(null);

  // Create ("Add Application") modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const emptyCreateForm = {
    app_id: '',
    app_name: '',
    app_description: '',
    app_status: 'ACTIVE',
    app_environment: '',
    app_type: '',
    app_version: '',
    app_owner: '',
    notes: '',
    compliance_flags: {}
  };
  const [createFormData, setCreateFormData] = useState(emptyCreateForm);

  // Derived data with defaults - wrapped in useMemo to prevent dependency changes
  const appList = React.useMemo(() => applications || [], [applications]);
  const complianceList = React.useMemo(() => complianceTypes || [], [complianceTypes]);
  const userList = React.useMemo(() => enterpriseUsers || [], [enterpriseUsers]);

  // Display error from fetch
  const displayError = fetchError?.message;

  // Apply filters to applications
  const filteredApplications = React.useMemo(() => {
    let filtered = [...appList];

    // Filter by application name
    if (filters.appName) {
      filtered = filtered.filter(app =>
        app.app_name?.toLowerCase().includes(filters.appName.toLowerCase())
      );
    }

    // Filter by status
    if (filters.status) {
      filtered = filtered.filter(app => app.app_status === filters.status);
    }

    // Filter by environment
    if (filters.environment) {
      filtered = filtered.filter(app => app.app_environment === filters.environment);
    }

    // Filter by app type
    if (filters.appType) {
      filtered = filtered.filter(app => app.app_type === filters.appType);
    }

    // Filter by compliance flags (multi-select with AND logic)
    if (filters.compliance_filters.length > 0) {
      filtered = filtered.filter(app => {
        try {
          const complianceFlags = typeof app.compliance_flags === 'string'
            ? JSON.parse(app.compliance_flags)
            : app.compliance_flags;
          return filters.compliance_filters.every(complianceType =>
            complianceFlags && complianceFlags[complianceType] === 'true'
          );
        } catch {
          return false;
        }
      });
    }

    return filtered;
  }, [appList, filters]);

  // Get unique values for filter dropdowns
  const getUniqueAppNames = () => {
    const appNames = [...new Set(appList.map(app => app.app_name))].filter(Boolean);
    return appNames.sort();
  };

  const getUniqueStatuses = () => {
    const knownStatuses = ['ACTIVE', 'INACTIVE', 'DEPRECATED', 'DECOMMISSIONED', 'BUILD'];
    const dataStatuses = appList
      .map(app => app.app_status ? String(app.app_status).trim() : null)
      .filter(Boolean);
    return [...new Set([...knownStatuses, ...dataStatuses])].sort();
  };

  const getUniqueEnvironments = () => {
    const knownEnvironments = ['PRODUCTION', 'DEVELOPMENT', 'STAGING', 'TEST'];
    const dataEnvironments = appList
      .map(app => app.app_environment ? String(app.app_environment).trim() : null)
      .filter(Boolean);
    return [...new Set([...knownEnvironments, ...dataEnvironments])].sort();
  };

  const getUniqueAppTypes = () => {
    const knownTypes = [
      'API Service', 'Batch System', 'Desktop Application', 'Development Tool',
      'Infrastructure', 'Mobile Application', 'Security Application', 'System Tool', 'Web Application'
    ];
    const dataTypes = appList
      .map(app => app.app_type ? String(app.app_type).trim() : null)
      .filter(Boolean);
    return [...new Set([...knownTypes, ...dataTypes])].sort();
  };

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilter(filterType, value);
  };

  // Toggle compliance filter
  const handleToggleComplianceFilter = (complianceKey) => {
    toggleArrayFilter('compliance_filters', complianceKey);
  };

  // Clear compliance filters
  const clearComplianceFilters = () => {
    setFilter('compliance_filters', []);
  };

  const handleEditApp = (app) => {
    setEditingApp(app);
    populateFormData(app);
    setShowEditModal(true);
  };

  // Helper function to parse compliance flags
  const parseComplianceFlags = (complianceString) => {
    try {
      if (!complianceString) return {};
      const parsed = typeof complianceString === 'string' ? JSON.parse(complianceString) : complianceString;
      const result = {};
      Object.keys(parsed).forEach(key => {
        result[key] = parsed[key] === 'true';
      });
      return result;
    } catch {
      return {};
    }
  };

  const populateFormData = (app) => {
    const complianceFlags = parseComplianceFlags(app.compliance_flags);
    setEditFormData({
      app_status: app.app_status || '',
      app_environment: app.app_environment || '',
      app_type: app.app_type || '',
      app_version: app.app_version || '',
      app_owner: app.app_owner || '',
      notes: app.notes || '',
      compliance_flags: complianceFlags
    });
  };

  const handleFormChange = (field, value) => {
    if (field.startsWith('compliance_')) {
      const complianceType = field.replace('compliance_', '');
      setEditFormData(prev => ({
        ...prev,
        compliance_flags: {
          ...prev.compliance_flags,
          [complianceType]: value
        }
      }));
    } else {
      setEditFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      const updateData = {};
      if (editFormData.app_status) updateData.app_status = editFormData.app_status;
      if (editFormData.app_environment) updateData.app_environment = editFormData.app_environment;
      if (editFormData.app_type) updateData.app_type = editFormData.app_type;
      if (editFormData.app_version) updateData.app_version = editFormData.app_version;
      // Always send app_owner: an empty value means "clear the owner" (the
      // backend NULLs it). Sending only-when-truthy made clearing impossible.
      updateData.app_owner = editFormData.app_owner || '';
      if (editFormData.notes) updateData.notes = editFormData.notes;

      const complianceUpdates = {};
      Object.keys(editFormData.compliance_flags).forEach(key => {
        complianceUpdates[key] = editFormData.compliance_flags[key] ? 'true' : 'false';
      });
      if (Object.keys(complianceUpdates).length > 0) {
        updateData.compliance_flags = complianceUpdates;
      }

      if (Object.keys(updateData).length === 0) {
        alert('No changes to save');
        setSaving(false);
        return;
      }

      await axios.put(
        API_CONFIG.getDatabasesEndpoint(`applications/${editingApp.app_id}/update/`),
        updateData
      );

      await fetchApplications();
      setShowEditModal(false);
      setEditingApp(null);
      alert(`Application ${editingApp.app_name} updated successfully`);

    } catch (err) {
      console.error('Save error:', err);
      alert('Failed to save changes: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenCreate = () => {
    setCreateFormData(emptyCreateForm);
    setShowCreateModal(true);
  };

  const handleCreateFormChange = (field, value) => {
    if (field.startsWith('compliance_')) {
      const complianceType = field.replace('compliance_', '');
      setCreateFormData(prev => ({
        ...prev,
        compliance_flags: {
          ...prev.compliance_flags,
          [complianceType]: value
        }
      }));
    } else {
      setCreateFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleCreateApp = async () => {
    // Client-side required-field validation (server enforces too)
    if (!createFormData.app_id.trim()) {
      alert('Application ID is required');
      return;
    }
    if (!createFormData.app_name.trim()) {
      alert('Application Name is required');
      return;
    }

    setCreating(true);
    try {
      const payload = {
        app_id: createFormData.app_id.trim(),
        app_name: createFormData.app_name.trim(),
        app_description: createFormData.app_description,
        app_status: createFormData.app_status,
        app_environment: createFormData.app_environment,
        app_type: createFormData.app_type,
        app_version: createFormData.app_version,
        app_owner: createFormData.app_owner,
        notes: createFormData.notes,
        compliance_flags: Object.keys(createFormData.compliance_flags).reduce((acc, key) => {
          acc[key] = createFormData.compliance_flags[key] ? 'true' : 'false';
          return acc;
        }, {})
      };

      const response = await axios.post(
        API_CONFIG.getDatabasesEndpoint('applications/create/'),
        payload
      );

      await fetchApplications();
      setShowCreateModal(false);
      const warning = response.data?.warning;
      alert(
        `Application ${payload.app_name} (${payload.app_id}) created successfully` +
        (warning ? `\n\n⚠ ${warning}` : '')
      );
    } catch (err) {
      console.error('Create error:', err);
      alert('Failed to create application: ' + (err.response?.data?.error || err.message));
    } finally {
      setCreating(false);
    }
  };

  const handleExportCSV = () => {
    const totalApps = appList.length;
    const confirmMessage =
      'Export Applications to CSV\n\n' +
      'This will export:\n' +
      `• Total applications: ${totalApps} (Export includes total applications not filtered)\n\n` +
      'Do you want to continue with the export?';

    if (window.confirm(confirmMessage)) {
      window.location.href = API_CONFIG.getDatabasesEndpoint('applications/export-csv/');
    }
  };

  const handleImportCSV = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      alert('Please select a CSV file');
      return;
    }

    if (!window.confirm(
      'Import CSV will:\n' +
      '• Add new applications from CSV\n' +
      '• Update existing applications\n' +
      '• Delete applications not in CSV\n\n' +
      'Do you want to continue?'
    )) {
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        API_CONFIG.getDatabasesEndpoint('applications/import-csv/'),
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.success) {
        alert(
          `Import completed successfully!\n\n` +
          `Added: ${response.data.added}\n` +
          `Updated: ${response.data.updated}\n` +
          `Deleted: ${response.data.deleted}\n` +
          `Total in CSV: ${response.data.total_in_csv}`
        );
        await fetchApplications();
      }
    } catch (err) {
      console.error('Import error:', err);
      alert('Import failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const renderComplianceFlags = (flags) => {
    if (!flags) return <span className="text-muted">No Compliance Data</span>;

    try {
      const parsed = typeof flags === 'string' ? JSON.parse(flags) : flags;
      const complianceMap = {};
      complianceList.forEach(ct => {
        complianceMap[ct.key] = { label: ct.label, color: ct.color };
      });

      const activeCompliance = Object.entries(parsed)
        .filter(([key, value]) => value === 'true' && complianceMap[key])
        .map(([key]) => complianceMap[key]);

      if (activeCompliance.length === 0) {
        return <span className="text-muted">No Compliance</span>;
      }

      // Map badge colors to text colors
      const colorMap = {
        'primary': 'text-primary',
        'success': 'text-success',
        'danger': 'text-danger',
        'warning': 'text-warning',
        'info': 'text-info',
        'secondary': 'text-secondary'
      };

      return (
        <div className="d-flex flex-wrap gap-2">
          {activeCompliance.map((compliance, index) => (
            <span key={index} className={colorMap[compliance.color] || 'text-secondary'} title={`${compliance.label} Compliant`}>
              <i className="fas fa-circle fa-xs me-1"></i>{compliance.label}
            </span>
          ))}
        </div>
      );
    } catch {
      return <span className="text-danger">Invalid Data</span>;
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      'ACTIVE': 'text-success',
      'INACTIVE': 'text-secondary',
      'DEPRECATED': 'text-warning',
      'DECOMMISSIONED': 'text-danger',
      'BUILD': 'text-info'
    };
    const statusClass = statusClasses[status] || 'text-muted';

    return (
      <span className={statusClass}>
        {status || 'Unknown'}
      </span>
    );
  };

  return (
    <div className="container-fluid mt-4">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="fas fa-layer-group me-2"></i>
                Application Details
                <span className="badge bg-secondary ms-2">
                  {filteredApplications.length} of {appList.length}
                </span>
              </h5>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={clearAllFilters}
                  disabled={loading}
                >
                  <i className="fas fa-filter me-1"></i>
                  Clear Filters
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleOpenCreate}
                  disabled={loading}
                >
                  <i className="fas fa-plus me-1"></i>
                  Add Application
                </button>
                <button
                  className="btn btn-success btn-sm"
                  onClick={handleExportCSV}
                  disabled={loading}
                >
                  <i className="fas fa-file-download me-1"></i>
                  Export CSV
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".csv"
                  onChange={handleImportCSV}
                  style={{ display: 'none' }}
                />
                <button
                  className="btn btn-warning btn-sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading || importing}
                >
                  {importing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                      Importing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-file-upload me-1"></i>
                      Import CSV
                    </>
                  )}
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={fetchApplications}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Loading...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-sync-alt me-2"></i>
                      Refresh Data
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Filter Panel */}
            <div className="card-body border-bottom bg-light">
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label fw-bold">Application Name</label>
                  <input
                    type="text"
                    className="form-select form-select-sm"
                    value={filters.appName}
                    onChange={(e) => handleFilterChange('appName', e.target.value)}
                    placeholder="Type or select Application..."
                    list="app-name-list"
                  />
                  <datalist id="app-name-list">
                    {getUniqueAppNames().map(appName => (
                      <option key={appName} value={appName} />
                    ))}
                  </datalist>
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-bold">Status</label>
                  <select
                    className="form-select form-select-sm"
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <option value="">All Statuses</option>
                    {getUniqueStatuses().map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-bold">Environment</label>
                  <select
                    className="form-select form-select-sm"
                    value={filters.environment}
                    onChange={(e) => handleFilterChange('environment', e.target.value)}
                  >
                    <option value="">All Environments</option>
                    {getUniqueEnvironments().map(env => (
                      <option key={env} value={env}>{env}</option>
                    ))}
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-bold">Application Type</label>
                  <select
                    className="form-select form-select-sm"
                    value={filters.appType}
                    onChange={(e) => handleFilterChange('appType', e.target.value)}
                  >
                    <option value="">All Types</option>
                    {getUniqueAppTypes().map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div className="col-12">
                  <label className="form-label fw-bold">
                    <i className="fas fa-filter me-1"></i>
                    Compliance Multi-Select Filter - Click on the Compliance Boxes below to filter applications
                  </label>
                  <div className="d-flex align-items-center flex-wrap gap-2">
                    <button
                      className={`btn btn-sm ${filters.compliance_filters.length === 0 ? 'btn-dark' : 'btn-outline-dark'}`}
                      onClick={clearComplianceFilters}
                      title="Clear all compliance filters"
                    >
                      <i className="fas fa-times me-1"></i>
                      Clear All
                    </button>
                    {complianceList.map(compliance => {
                      const isSelected = filters.compliance_filters.includes(compliance.key);
                      return (
                        <button
                          key={compliance.key}
                          className={`btn btn-sm ${
                            isSelected ? `btn-${compliance.color}` : `btn-outline-${compliance.color}`
                          }`}
                          style={{ border: '2px solid' }}
                          onClick={() => handleToggleComplianceFilter(compliance.key)}
                          title={`${isSelected ? 'Remove' : 'Add'} ${compliance.label} compliance filter`}
                        >
                          {isSelected && <i className="fas fa-check me-1"></i>}
                          {compliance.label}
                        </button>
                      );
                    })}
                  </div>
                  {filters.compliance_filters.length > 0 && (
                    <div className="mt-2">
                      <small className="text-muted">
                        <i className="fas fa-info-circle me-1"></i>
                        Showing applications with ALL selected compliance types: {filters.compliance_filters.map(key =>
                          complianceList.find(c => c.key === key)?.label
                        ).join(' + ')}
                        {filters.compliance_filters.length > 1 && (
                          <span className="ms-1 text-info" title="Applications must have ALL selected compliance types">
                            (AND logic)
                          </span>
                        )}
                      </small>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="card-body">
              {displayError && (
                <div className="alert alert-danger" role="alert">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {displayError}
                </div>
              )}

              {loading && appList.length === 0 ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading application details...</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-hover">
                    <thead className="table-dark">
                      <tr>
                        <th>App ID</th>
                        <th>Application Name</th>
                        <th>Version</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Environment</th>
                        <th>Owner</th>
                        <th>Compliance</th>
                        <th>Created Date</th>
                        <th>Description</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredApplications.length > 0 ? (
                        filteredApplications.map((app) => (
                          <tr key={app.app_id}>
                            <td><code>{app.app_id}</code></td>
                            <td><strong>{app.app_name}</strong></td>
                            <td>{app.app_version || 'N/A'}</td>
                            <td>{app.app_type || 'N/A'}</td>
                            <td>{getStatusBadge(app.app_status)}</td>
                            <td>
                              <span className={app.app_environment === 'PRODUCTION' ? 'text-danger' : 'text-info'}>
                                {app.app_environment || 'N/A'}
                              </span>
                            </td>
                            <td>
                              {app.owner_name ? (
                                <div>
                                  <div className="fw-bold">{app.owner_name}</div>
                                  <small className="text-muted">{app.app_owner}</small>
                                </div>
                              ) : app.app_owner ? (
                                /* app_owner didn't resolve to an enterprise user (owner_name empty):
                                   never present the raw stored value as if it were the owner */
                                <span
                                  className="text-warning"
                                  title={`Stored owner '${app.app_owner}' is not a verified enterprise user. Edit the application and select an owner from the dropdown.`}
                                >
                                  <i className="fas fa-exclamation-triangle me-1"></i>Unverified
                                </span>
                              ) : (
                                <span className="text-muted">N/A</span>
                              )}
                            </td>
                            <td>{renderComplianceFlags(app.compliance_flags)}</td>
                            <td>{formatDate(app.created_date)}</td>
                            <td>
                              <div style={{ maxWidth: '200px' }}>
                                {app.app_description ? (
                                  <span className="text-truncate d-block" title={app.app_description}>
                                    {app.app_description.length > 50
                                      ? app.app_description.substring(0, 50) + '...'
                                      : app.app_description
                                    }
                                  </span>
                                ) : (
                                  <span className="text-muted">No description</span>
                                )}
                              </div>
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => handleEditApp(app)}
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="11" className="text-center py-4">
                            <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                            <p className="text-muted">No application details found</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {appList.length > 0 && (
                <div className="mt-3">
                  <small className="text-muted">
                    <i className="fas fa-info-circle me-1"></i>
                    Showing {filteredApplications.length} of {appList.length} applications
                    {filteredApplications.length !== appList.length && (
                      <span className="text-primary"> (filtered)</span>
                    )}
                  </small>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1050, overflow: 'auto' }}>
          <div style={{ padding: '20px' }}>
            <div className="modal-content" style={{ maxWidth: '1400px', width: '100%', margin: '0 auto' }}>
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">
                  <i className="fas fa-edit me-2"></i>
                  Edit Application: {editingApp?.app_name}
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowEditModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>Application:</strong> {editingApp?.app_name} ({editingApp?.app_id})
                  <br />
                  <small>Current values are pre-filled. Modify the fields you want to update.</small>
                </div>

                <form>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        value={editFormData.app_status}
                        onChange={(e) => handleFormChange('app_status', e.target.value)}
                      >
                        <option value="">Keep current values</option>
                        <option value="ACTIVE">Active</option>
                        <option value="INACTIVE">Inactive</option>
                        <option value="DEPRECATED">Deprecated</option>
                        <option value="DECOMMISSIONED">Decommissioned</option>
                        <option value="BUILD">Build</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Environment</label>
                      <select
                        className="form-select"
                        value={editFormData.app_environment}
                        onChange={(e) => handleFormChange('app_environment', e.target.value)}
                      >
                        <option value="">Keep current values</option>
                        <option value="PRODUCTION">Production</option>
                        <option value="DEVELOPMENT">Development</option>
                        <option value="STAGING">Staging</option>
                        <option value="TEST">Test</option>
                      </select>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Application Type</label>
                      <select
                        className="form-select"
                        value={editFormData.app_type}
                        onChange={(e) => handleFormChange('app_type', e.target.value)}
                      >
                        <option value="">Keep current values</option>
                        <option value="Web Application">Web Application</option>
                        <option value="Desktop Application">Desktop Application</option>
                        <option value="Mobile Application">Mobile Application</option>
                        <option value="API Service">API Service</option>
                        <option value="Batch System">Batch System</option>
                        <option value="Security Application">Security Application</option>
                        <option value="System Tool">System Tool</option>
                        <option value="Infrastructure">Infrastructure</option>
                        <option value="Development Tool">Development Tool</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Version</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Keep current values"
                        value={editFormData.app_version}
                        onChange={(e) => handleFormChange('app_version', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-12">
                      <label className="form-label">Application Owner</label>
                      <select
                        className="form-select"
                        value={editFormData.app_owner}
                        onChange={(e) => handleFormChange('app_owner', e.target.value)}
                      >
                        <option value="">Select an owner...</option>
                        {userList.map(user => (
                          <option key={user.employee_id} value={user.employee_id}>
                            {user.display_name} ({user.employee_id})
                          </option>
                        ))}
                      </select>
                      <small className="text-muted">
                        <i className="fas fa-info-circle me-1"></i>
                        Select the owner from enterprise users
                      </small>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      <i className="fas fa-shield-alt me-2"></i>
                      Compliance Flags
                    </label>
                    <div className="row">
                      {complianceList.length > 0 ? (
                        complianceList.map((compliance) => (
                          <div key={compliance.key} className={`col-md-${complianceList.length <= 4 ? '6' : '4'} mb-2`}>
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`${compliance.key}-bulk`}
                                checked={editFormData.compliance_flags[compliance.key] || false}
                                onChange={(e) => handleFormChange(`compliance_${compliance.key}`, e.target.checked)}
                              />
                              <label className="form-check-label" htmlFor={`${compliance.key}-bulk`}>
                                <span className={`text-${compliance.color === 'warning' ? 'warning' : compliance.color} me-2`}>
                                  <i className="fas fa-circle fa-xs me-1"></i>{compliance.label}
                                </span>
                                Compliant
                              </label>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="col-12">
                          <div className="alert alert-warning">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            No compliance frameworks found.
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Notes</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      placeholder="Keep current values or add notes..."
                      value={editFormData.notes}
                      onChange={(e) => handleFormChange('notes', e.target.value)}
                    />
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveChanges}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-1"></i>
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create ("Add Application") Modal */}
      {showCreateModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1050, overflow: 'auto' }}>
          <div style={{ padding: '20px' }}>
            <div className="modal-content" style={{ maxWidth: '1400px', width: '100%', margin: '0 auto' }}>
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">
                  <i className="fas fa-plus me-2"></i>
                  Add Application
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowCreateModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  Register a new application. <strong>Application ID</strong> and{' '}
                  <strong>Application Name</strong> are required. Select an owner from your
                  enterprise users so attestations route to the right person.
                  {userList.length === 0 && (
                    <div className="mt-2 text-warning">
                      <i className="fas fa-exclamation-triangle me-1"></i>
                      No enterprise users are loaded yet — sync your IdP (Enterprise Users — Sync)
                      first so you can assign a verified owner.
                    </div>
                  )}
                </div>

                <form>
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label className="form-label fw-bold">Application ID <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. APP001"
                        maxLength={50}
                        value={createFormData.app_id}
                        onChange={(e) => handleCreateFormChange('app_id', e.target.value)}
                      />
                      <small className="text-muted">Unique identifier (max 50 chars).</small>
                    </div>
                    <div className="col-md-8">
                      <label className="form-label fw-bold">Application Name <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. Finance General Ledger"
                        maxLength={200}
                        value={createFormData.app_name}
                        onChange={(e) => handleCreateFormChange('app_name', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      placeholder="What is this application?"
                      value={createFormData.app_description}
                      onChange={(e) => handleCreateFormChange('app_description', e.target.value)}
                    />
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        value={createFormData.app_status}
                        onChange={(e) => handleCreateFormChange('app_status', e.target.value)}
                      >
                        <option value="ACTIVE">Active</option>
                        <option value="INACTIVE">Inactive</option>
                        <option value="DEPRECATED">Deprecated</option>
                        <option value="DECOMMISSIONED">Decommissioned</option>
                        <option value="BUILD">Build</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Environment</label>
                      <select
                        className="form-select"
                        value={createFormData.app_environment}
                        onChange={(e) => handleCreateFormChange('app_environment', e.target.value)}
                      >
                        <option value="">Not specified</option>
                        <option value="PRODUCTION">Production</option>
                        <option value="DEVELOPMENT">Development</option>
                        <option value="STAGING">Staging</option>
                        <option value="TEST">Test</option>
                      </select>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Application Type</label>
                      <select
                        className="form-select"
                        value={createFormData.app_type}
                        onChange={(e) => handleCreateFormChange('app_type', e.target.value)}
                      >
                        <option value="">Not specified</option>
                        <option value="Web Application">Web Application</option>
                        <option value="Desktop Application">Desktop Application</option>
                        <option value="Mobile Application">Mobile Application</option>
                        <option value="API Service">API Service</option>
                        <option value="Batch System">Batch System</option>
                        <option value="Security Application">Security Application</option>
                        <option value="System Tool">System Tool</option>
                        <option value="Infrastructure">Infrastructure</option>
                        <option value="Development Tool">Development Tool</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Version</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. 1.0.0"
                        value={createFormData.app_version}
                        onChange={(e) => handleCreateFormChange('app_version', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-12">
                      <label className="form-label">Application Owner</label>
                      <select
                        className="form-select"
                        value={createFormData.app_owner}
                        onChange={(e) => handleCreateFormChange('app_owner', e.target.value)}
                      >
                        <option value="">Select an owner...</option>
                        {userList.map(user => (
                          <option key={user.employee_id} value={user.employee_id}>
                            {user.display_name} ({user.employee_id})
                          </option>
                        ))}
                      </select>
                      <small className="text-muted">
                        <i className="fas fa-info-circle me-1"></i>
                        The owner must be an enterprise user; they receive the attestation requests for this application's databases.
                      </small>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      <i className="fas fa-shield-alt me-2"></i>
                      Compliance Flags
                    </label>
                    <div className="row">
                      {complianceList.length > 0 ? (
                        complianceList.map((compliance) => (
                          <div key={compliance.key} className={`col-md-${complianceList.length <= 4 ? '6' : '4'} mb-2`}>
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`${compliance.key}-create`}
                                checked={createFormData.compliance_flags[compliance.key] || false}
                                onChange={(e) => handleCreateFormChange(`compliance_${compliance.key}`, e.target.checked)}
                              />
                              <label className="form-check-label" htmlFor={`${compliance.key}-create`}>
                                <span className={`text-${compliance.color === 'warning' ? 'warning' : compliance.color} me-2`}>
                                  <i className="fas fa-circle fa-xs me-1"></i>{compliance.label}
                                </span>
                                Compliant
                              </label>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="col-12">
                          <div className="alert alert-warning">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            No compliance frameworks found.
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Notes</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      placeholder="Optional notes..."
                      value={createFormData.notes}
                      onChange={(e) => handleCreateFormChange('notes', e.target.value)}
                    />
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleCreateApp}
                  disabled={creating}
                >
                  {creating ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Creating...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-plus me-1"></i>
                      Create Application
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationDetails;
